import * as React from 'react';
import DatePicker from 'react-datepicker';
import './ReportFields.scss';

export interface Option {
  value: string;
  label: string;
}

const P = { strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' } as const;
const Icon = ({ d, size = 16 }: { d: string; size?: number }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" {...P}><path d={d} /></svg>
);

export const REPORT_FIELD_ICONS = {
  calendar: 'M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z',
  check: 'M20 6 9 17l-5-5',
  help: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20M9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3M12 17h.01',
  info: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20M12 10v7M12 7h.01',
  warning: 'M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0zM12 9v4M12 17h.01',
  arrowRight: 'M5 12h14M13 5l7 7-7 7',
  list: 'M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01',
  users: 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75',
  file: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6',
  target: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20M12 18a6 6 0 1 0 0-12 6 6 0 0 0 0 12M12 14a2 2 0 1 0 0-4 2 2 0 0 0 0 4'
};

export const ReportIcon = ({ name, size = 16 }: { name: keyof typeof REPORT_FIELD_ICONS; size?: number }) => (
  <Icon d={REPORT_FIELD_ICONS[name]} size={size} />
);

export const FormSection = ({ label, icon, count, className = '', children }: {
  label?: React.ReactNode;
  icon?: React.ReactNode;
  count?: React.ReactNode;
  className?: string;
  children?: React.ReactNode;
}) => (
  <div className={['sit-rf__section', className].filter(Boolean).join(' ')}>
    {label != null && <div className="sit-rf__seclabel">{icon}{label}{count != null && <span className="sit-rf__count">- {count}</span>}</div>}
    {children}
  </div>
);

export const HintTip = ({ text }: { text?: string }) => {
  if (!text) return null;
  return <span className="sit-rf__hint" tabIndex={0} role="note"><ReportIcon name="help" size={14} /><span className="sit-rf__hint-bubble">{text}</span></span>;
};

export const ToggleRow = ({ label, hint, checked, onChange, className = '' }: {
  label: React.ReactNode;
  hint?: string;
  checked?: boolean;
  onChange?: (next: boolean) => void;
  className?: string;
}) => (
  <button type="button" className={['sit-rf__toggle', className].filter(Boolean).join(' ')} onClick={() => onChange && onChange(!checked)}>
    <span className="sit-rf__toggle-text"><span className="sit-rf__toggle-label">{label}</span><HintTip text={hint} /></span>
    <span className={['sit-rf__sw', checked ? 'sit-rf__sw--on' : ''].join(' ')}><span className="sit-rf__sw-thumb" /></span>
  </button>
);

interface ToggleDef { key: string; label: React.ReactNode; hint?: string; }
export const ToggleGroup = ({ toggles, value, onChange }: {
  toggles: ToggleDef[];
  value: Record<string, boolean>;
  onChange: (next: Record<string, boolean>) => void;
}) => (
  <div className="sit-rf__toggles">
    {toggles.map(toggle => (
      <ToggleRow key={toggle.key} label={toggle.label} hint={toggle.hint} checked={Boolean(value[toggle.key])} onChange={next => onChange({ ...value, [toggle.key]: next })} />
    ))}
  </div>
);

interface PeriodPreset { value: string; label: string; icon?: React.ReactNode; hint?: string; }
export const PeriodField = ({ presets, value, onChange }: {
  presets: PeriodPreset[];
  value: string;
  onChange: (next: string) => void;
}) => (
  <div className="sit-rf__periods">
    {presets.map(preset => (
      <button type="button" key={preset.value} className={['sit-rf__period', value === preset.value ? 'sit-rf__period--on' : ''].join(' ')} onClick={() => onChange(preset.value)}>
        {preset.icon && <span className="sit-rf__period-ico">{preset.icon}</span>}
        <span className="sit-rf__period-label">{preset.label}{preset.hint ? <HintTip text={preset.hint} /> : null}</span>
      </button>
    ))}
  </div>
);

const formatDate = (date?: Date) => date ? date.toLocaleDateString('en-GB') : '';
interface QuickRange { label: string; range: { from: Date; to: Date }; }
export const DateRangeField = ({ from, to, onFromChange, onToChange, quickRanges = [] }: {
  from?: Date;
  to?: Date;
  onFromChange: (next: Date) => void;
  onToChange: (next: Date) => void;
  quickRanges?: QuickRange[];
}) => {
  const isActive = (range: QuickRange) => from && to && formatDate(from) === formatDate(range.range.from) && formatDate(to) === formatDate(range.range.to);
  return (
    <div className="sit-rf__datewrap">
      <div className="sit-rf__daterange">
        <div className="sit-rf__datefield">
          <span className="sit-rf__datefield-label">From</span>
          <div className="sit-rf__dateinput"><span className="sit-rf__dateinput-ico"><ReportIcon name="calendar" /></span><DatePicker selected={from} onChange={(date: Date) => date && onFromChange(date)} className="sit-rf__datepicker" dateFormat="dd-MM-yyyy" /></div>
        </div>
        <div className="sit-rf__daterange-sep"><ReportIcon name="arrowRight" /></div>
        <div className="sit-rf__datefield">
          <span className="sit-rf__datefield-label">To</span>
          <div className="sit-rf__dateinput"><span className="sit-rf__dateinput-ico"><ReportIcon name="calendar" /></span><DatePicker selected={to} minDate={from} onChange={(date: Date) => date && onToChange(date)} className="sit-rf__datepicker" dateFormat="dd-MM-yyyy" /></div>
        </div>
      </div>
      {quickRanges.length > 0 && <div className="sit-rf__quickdates">{quickRanges.map(range => <button type="button" key={range.label} className={['sit-rf__quickdate', isActive(range) ? 'sit-rf__quickdate--on' : ''].join(' ')} onClick={() => { onFromChange(range.range.from); onToChange(range.range.to); }}>{range.label}</button>)}</div>}
    </div>
  );
};

export const ChipSelect = ({ options, value, onChange, multiple = false, selectAll = false, warn }: {
  options: Option[];
  value: string[] | string;
  onChange: (next: string[] | string) => void;
  multiple?: boolean;
  selectAll?: boolean;
  warn?: string;
}) => {
  const values = multiple && Array.isArray(value) ? value : [];
  const isSelected = (optionValue: string) => multiple ? values.includes(optionValue) : value === optionValue;
  const isAllSelected = multiple && values.length === options.length;
  const toggle = (optionValue: string) => {
    if (!multiple) {
      onChange(optionValue);
      return;
    }
    onChange(values.includes(optionValue) ? values.filter(item => item !== optionValue) : [...values, optionValue]);
  };
  const hasNoValue = multiple ? values.length === 0 : !value;
  return (
    <div>
      {multiple && selectAll && <button type="button" className="sit-rf__selectall" onClick={() => onChange(isAllSelected ? [] : options.map(option => option.value))}><span className={['sit-rf__selbox', isAllSelected ? 'sit-rf__selbox--on' : ''].join(' ')}>{isAllSelected && <ReportIcon name="check" size={12} />}</span>{isAllSelected ? 'Clear all' : 'Select all'}</button>}
      <div className="sit-rf__chips">{options.map(option => <button type="button" key={option.value} className={['sit-rf__chip', isSelected(option.value) ? 'sit-rf__chip--on' : ''].join(' ')} onClick={() => toggle(option.value)}><span className="sit-rf__chip-check"><ReportIcon name="check" size={14} /></span>{option.label}</button>)}</div>
      {hasNoValue && warn && <div className="sit-rf__warn"><ReportIcon name="warning" size={13} /> {warn}</div>}
    </div>
  );
};
